import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchRedesign, deleteRedesign, updateRedesign } from '@/services/redesigns';
import { RedesignDesignSection } from '@/components/project/RedesignDesignSection';
import { REDESIGN_STATUS_COLORS } from '@/types';
import type { RedesignStatus } from '@/types';

function parseProjectId(projectId: string): { id: string } | null {
  if (projectId.startsWith('redesign-')) return { id: projectId.slice(9) };
  // Legacy pack URLs — treat as invalid
  if (projectId.startsWith('pack-')) return null;
  return null;
}

/* ── Confirm-delete dialog ────────────────────────────────── */

function ConfirmDeleteDialog({ projectName, typeName, isDeleting, onConfirm, onCancel }: {
  projectName: string;
  typeName: string;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const requiredPhrase = 'delete';
  const canConfirm = typed.toLowerCase() === requiredPhrase && !isDeleting;

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !isDeleting) onCancel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel, isDeleting]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !isDeleting) onCancel(); }}
    >
      <div style={{
        backgroundColor: 'var(--color-card-surface)', borderRadius: '12px', padding: '24px',
        width: '100%', maxWidth: '440px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        {/* Icon + title */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '50%',
            backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 6v4m0 4h.01M3 10a7 7 0 1114 0 7 7 0 01-14 0z"
                stroke="#dc2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>
              Delete {typeName}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              This will permanently delete <strong style={{ color: 'var(--text-primary)' }}>{projectName}</strong> and
              all its artifacts including screenshots, tasks, and revisions. This action cannot be undone.
            </p>
          </div>
        </div>

        {/* What gets deleted */}
        <div style={{
          padding: '10px 12px', borderRadius: '8px',
          backgroundColor: '#fef2f2', border: '1px solid #fecaca',
          marginBottom: '16px',
        }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>
            The following will be deleted:
          </div>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#991b1b', lineHeight: 1.8 }}>
            {typeName === 'handoff pack' ? (
              <>
                <li>All pack items and their screenshots</li>
                <li>Design revisions and implementation jobs</li>
                <li>Captured screenshot files from disk</li>
              </>
            ) : (
              <>
                <li>All redesign pages and their screenshots</li>
                <li>Redesign tasks and code exports</li>
                <li>Uploaded screenshot files from disk</li>
              </>
            )}
          </ul>
        </div>

        {/* Type to confirm */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>
            Type <span style={{
              fontFamily: 'monospace', padding: '1px 5px',
              backgroundColor: 'var(--bg-secondary)', borderRadius: '4px', fontSize: '12px',
            }}>{requiredPhrase}</span> to confirm
          </label>
          <input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm) onConfirm(); }}
            placeholder={requiredPhrase}
            disabled={isDeleting}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: '6px',
              fontSize: '13px', outline: 'none',
            }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={onCancel}
            disabled={isDeleting}
            style={{
              padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
              cursor: 'pointer', border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-card-surface)', color: 'var(--text-secondary)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm}
            style={{
              padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              border: 'none',
              backgroundColor: canConfirm ? '#dc2626' : '#fca5a5',
              color: '#fff',
              opacity: canConfirm ? 1 : 0.7,
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main component ───────────────────────────────────────── */

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const parsed = useMemo(() => projectId ? parseProjectId(projectId) : null, [projectId]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');

  const deleteRedesignMutation = useMutation({
    mutationFn: (id: string) => deleteRedesign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redesigns'] });
      navigate('/ui-orchestrator');
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateRedesign(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['redesign', parsed?.id] });
      setIsEditingName(false);
    },
  });

  const handleConfirmDelete = useCallback(() => {
    if (!parsed) return;
    deleteRedesignMutation.mutate(parsed.id);
  }, [parsed, deleteRedesignMutation]);

  const isDeleting = deleteRedesignMutation.isPending;
  const deleteError = deleteRedesignMutation.error;

  const { data: redesign } = useQuery({
    queryKey: ['redesign', parsed?.id],
    queryFn: () => fetchRedesign(parsed!.id),
    enabled: !!parsed,
    refetchInterval: (query) => {
      const d = query.state.data;
      const terminal = ['failed', 'captured', 'analyzed'];
      if (d && terminal.includes(d.status)) return false;
      return 5000;
    },
  });

  if (!parsed) return <p style={{ color: 'var(--color-error)' }}>Invalid project URL</p>;

  if (!redesign) return <p style={{ color: 'var(--text-muted)' }}>Loading...</p>;

  const title = redesign.name || 'Untitled';
  const repo = redesign.repo;
  const status = redesign.status;
  const statusColor = REDESIGN_STATUS_COLORS[status as RedesignStatus] ?? 'var(--bg-disabled)';

  return (
    <div>
      {/* Delete confirmation dialog */}
      {showDeleteDialog && (
        <ConfirmDeleteDialog
          projectName={title}
          typeName="redesign"
          isDeleting={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteDialog(false)}
        />
      )}

      {/* Header */}
      <section style={{ marginBottom: 40 }}>
        {/* Back + Delete row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <button
            onClick={() => navigate('/ui-orchestrator')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              fontSize: 13,
              fontWeight: 500,
              padding: '4px 8px',
              borderRadius: 8,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
            Projects
          </button>
          <button
            onClick={() => setShowDeleteDialog(true)}
            disabled={isDeleting}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              background: 'transparent',
              border: 'none',
              borderRadius: '50%',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              color: '#94a3b8',
              opacity: isDeleting ? 0.5 : 1,
              transition: 'background 150ms',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>delete</span>
          </button>
        </div>

        {/* Title + badges */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            {isEditingName ? (
              <input
                autoFocus
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={() => {
                  if (editName.trim() && editName !== title) {
                    renameMutation.mutate({ id: parsed.id, name: editName.trim() });
                  } else {
                    setIsEditingName(false);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editName.trim()) {
                    renameMutation.mutate({ id: parsed.id, name: editName.trim() });
                  } else if (e.key === 'Escape') {
                    setIsEditingName(false);
                  }
                }}
                style={{
                  fontSize: 28, fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-primary)',
                  margin: '0 0 6px', lineHeight: 1.2, border: 'none', borderBottom: '2px solid var(--color-primary)',
                  outline: 'none', background: 'transparent', width: '100%', padding: 0,
                  fontFamily: 'inherit',
                }}
              />
            ) : (
              <h1
                onClick={() => {
                  setEditName(title);
                  setIsEditingName(true);
                }}
                style={{
                  fontSize: 28, fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-primary)',
                  margin: '0 0 6px', lineHeight: 1.2, cursor: 'text',
                }}
                title="Click to edit name"
              >
                {title}
              </h1>
            )}
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', margin: 0 }}>
              {repo}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span
              style={{
                padding: '4px 14px',
                borderRadius: 9999,
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#fff',
                background: 'linear-gradient(135deg, #4a40e0 0%, #9795ff 100%)',
              }}
            >
              Redesign
            </span>
            <span style={{ padding: '4px 14px', borderRadius: 9999, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#fff', backgroundColor: statusColor }}>
              {status}
            </span>
          </div>
        </div>
      </section>

      {deleteError && (
        <div style={{ padding: '8px 12px', marginBottom: '12px', borderRadius: '6px', backgroundColor: '#fef2f2', color: '#dc2626', fontSize: '12px' }}>
          Delete failed: {(deleteError as Error).message}
        </div>
      )}

      {/* Content */}
      <RedesignDesignSection redesign={redesign} />
    </div>
  );
}
